import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  Fab,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { mobileBeneficiaryService } from '../../../services/mobile/mobileBeneficiaryService';

const statusFilters = ['All', 'Active', 'Inactive'];

const MobileBeneficiaryList = () => {
  const navigate = useNavigate();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;
  const debounceRef = useRef(null);

  const fetchBeneficiaries = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = { PageSize: pageSize, PageNumber: pageNum };
      if (activeFilter === 'Active') params.isActive = true;
      else if (activeFilter === 'Inactive') params.isActive = false;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const result = await mobileBeneficiaryService.getBeneficiaries(params);

      if (result.success) {
        const data = Array.isArray(result.data) ? result.data : [];
        if (append) {
          setBeneficiaries((prev) => [...prev, ...data]);
        } else {
          setBeneficiaries(data);
        }
        const totalCount = result.TotalCount || result.totalCount || 0;
        setHasMore(pageNum * pageSize < totalCount);
      }
    } catch {
      // handled by service layer
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeFilter, searchQuery]);

  useEffect(() => {
    setPage(1);
    fetchBeneficiaries(1, false);
  }, [fetchBeneficiaries]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchBeneficiaries(nextPage, true);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
        Beneficiaries
      </Typography>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search beneficiaries..."
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8' }} />
            </InputAdornment>
          ),
          sx: { borderRadius: 2, height: 48 }
        }}
        sx={{ mb: 2 }}
      />

      {/* Status Filter Chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
        {statusFilters.map((filter) => (
          <Chip
            key={filter}
            label={filter}
            onClick={() => handleFilterChange(filter)}
            variant={activeFilter === filter ? 'filled' : 'outlined'}
            color={activeFilter === filter ? 'primary' : 'default'}
            sx={{
              flexShrink: 0,
              fontWeight: activeFilter === filter ? 600 : 400,
              ...(activeFilter === filter && {
                backgroundColor: '#2563eb',
                color: '#fff'
              })
            }}
          />
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#2563eb' }} />
        </Box>
      ) : beneficiaries.length === 0 ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No beneficiaries found.
          </Typography>
        </Card>
      ) : (
        <>
          {beneficiaries.map((beneficiary, index) => {
            const id = beneficiary.Id || beneficiary.id;
            const name = beneficiary.BeneficiaryName || beneficiary.beneficiaryName || 'Unknown';
            const isActive = beneficiary.IsActive !== undefined ? beneficiary.IsActive : beneficiary.isActive;
            const createdDate = beneficiary.CreatedDate || beneficiary.createdDate;
            const createdUser = beneficiary.CreatedUser || beneficiary.createdUser;

            return (
              <Card
                key={id || index}
                elevation={0}
                onClick={() => navigate(`/mobile/beneficiaries/${id}`)}
                sx={{
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  mb: 1.5,
                  cursor: 'pointer',
                  '&:active': { backgroundColor: '#f1f5f9' }
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a', flex: 1, mr: 1 }}>
                      {name}
                    </Typography>
                    <Chip
                      label={isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={isActive ? 'success' : 'default'}
                      sx={{ height: 24, fontSize: '0.75rem' }}
                    />
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {createdUser || ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {formatDate(createdDate)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            );
          })}

          {hasMore && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <EasyCheckButton
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{
                  borderRadius: 2,
                  color: '#2563eb',
                  borderColor: '#2563eb',
                  '&:hover': { borderColor: '#1d4ed8', backgroundColor: '#eff6ff' }
                }}
                variant="outlined"
              >
                {loadingMore ? <CircularProgress size={20} /> : 'Load More'}
              </EasyCheckButton>
            </Box>
          )}
        </>
      )}

      {/* FAB */}
      <Fab
        color="primary"
        onClick={() => navigate('/mobile/beneficiaries/new')}
        sx={{
          position: 'fixed',
          bottom: 72,
          right: 16,
          backgroundColor: '#2563eb',
          '&:hover': { backgroundColor: '#1d4ed8' }
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default MobileBeneficiaryList;
